/**
 * Audio Visualizer Component
 * Displays real-time audio waveform visualization during playback
 */

import React, { useEffect, useRef, useState } from 'react';
import { applyAudioCrossOrigin, isCrossOriginMediaUrl } from '../../utils/audioPlaybackReady';

interface AudioVisualizerProps {
  audioElement: HTMLAudioElement | null;
  isPlaying: boolean;
  height?: number;
  barCount?: number;
  className?: string;
}

// Global WeakMap to track audio elements and their connections
// This ensures we only create one MediaElementSourceNode per audio element
const audioElementConnections = new WeakMap<HTMLAudioElement, {
  audioContext: AudioContext;
  source: MediaElementAudioSourceNode;
  analyser: AnalyserNode;
}>();

// Track which audio elements should use visualization (to avoid blocking default output)
const visualizationEnabled = new WeakSet<HTMLAudioElement>();

export const AudioVisualizer: React.FC<AudioVisualizerProps> = ({
  audioElement,
  isPlaying,
  height = 60,
  barCount = 32,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const lastFrameRef = useRef<Uint8Array | null>(null); // Store last frame when paused
  const [isInitialized, setIsInitialized] = useState(false);
  const [canVisualize, setCanVisualize] = useState(true);

  useEffect(() => {
    // Clear last frame when audio ends or is reset (only if audio element exists)
    let cleanup: (() => void) | null = null;
    if (audioElement) {
      const handleEnded = () => {
        lastFrameRef.current = null;
      };

      const handleLoadStart = () => {
        lastFrameRef.current = null;
      };

      audioElement.addEventListener('ended', handleEnded);
      audioElement.addEventListener('loadstart', handleLoadStart);

      cleanup = () => {
        audioElement.removeEventListener('ended', handleEnded);
        audioElement.removeEventListener('loadstart', handleLoadStart);
      };
    }

    // Initialize audio context and analyser
    const initAudioAnalysis = async () => {
      try {
        // Check if this audio element already has a connection
        const existingConnection = audioElementConnections.get(audioElement);
        if (existingConnection) {
          // Reuse existing connection
          audioContextRef.current = existingConnection.audioContext;
          sourceRef.current = existingConnection.source;
          analyserRef.current = existingConnection.analyser;
          
          // Resume if suspended
          if (existingConnection.audioContext.state === 'suspended') {
            await existingConnection.audioContext.resume();
          }
          
          setIsInitialized(true);
          return;
        }

        // Check if we already have refs set (from previous render)
        if (audioContextRef.current && sourceRef.current && analyserRef.current) {
          // Store in WeakMap for future reuse
          audioElementConnections.set(audioElement, {
            audioContext: audioContextRef.current,
            source: sourceRef.current,
            analyser: analyserRef.current,
          });
          
          if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
          }
          
          setIsInitialized(true);
          return;
        }

        // Create new connection only if visualization is enabled for this element
        // Mark this element as using visualization
        visualizationEnabled.add(audioElement);
        
        try {
          // Web Audio needs CORS mode only for cross-origin media. Changing
          // crossOrigin after src is set forces a reload — avoid that for same-origin.
          const src = audioElement.src || '';
          if (src && isCrossOriginMediaUrl(src) && audioElement.crossOrigin !== 'anonymous') {
            applyAudioCrossOrigin(audioElement, src);
            // Reload without clearing src — clearing briefly leaves no supported sources.
            audioElement.load();
            await new Promise<void>((resolve) => {
              if (audioElement.readyState >= HTMLMediaElement.HAVE_METADATA) {
                resolve();
                return;
              }
              const done = () => {
                audioElement.removeEventListener('loadedmetadata', done);
                audioElement.removeEventListener('error', done);
                resolve();
              };
              audioElement.addEventListener('loadedmetadata', done);
              audioElement.addEventListener('error', done);
            });
          }
          
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          
          // Resume audio context if suspended (required for user interaction)
          if (audioContext.state === 'suspended') {
            await audioContext.resume();
          }
          
          const source = audioContext.createMediaElementSource(audioElement);
          const analyser = audioContext.createAnalyser();
          
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.8;
          
          // Connect: source -> analyser -> destination
          // Note: createMediaElementSource automatically disconnects the audio element
          // from its default output, so we must route through our graph
          source.connect(analyser);
          analyser.connect(audioContext.destination);
          
          // Store references
          audioContextRef.current = audioContext;
          sourceRef.current = source;
          analyserRef.current = analyser;
          
          // Store in WeakMap for reuse
          audioElementConnections.set(audioElement, {
            audioContext,
            source,
            analyser,
          });
          
          setIsInitialized(true);
        } catch (createError) {
          // If error is about already connected, disable visualization
          if (createError instanceof Error && createError.message.includes('already connected')) {
            console.warn('Audio element already connected, disabling visualization');
            setCanVisualize(false);
            setIsInitialized(false);
            visualizationEnabled.delete(audioElement);
            return;
          }
          throw createError;
        }
      } catch (error) {
        console.error('Failed to initialize audio analysis:', error);
        setCanVisualize(false);
        setIsInitialized(false);
      }
    };

    // Initialize when audio element is ready (not just when playing)
    // This allows visualization to be ready before playback starts
    // Initialize when playing OR when audio is ready (to show watermark)
    if (audioElement) {
      if (!isInitialized && (isPlaying || audioElement.readyState >= 2)) {
        // Create connection when playing, or just prepare for watermark display
        if (isPlaying) {
          initAudioAnalysis().then(() => {
            // Ensure audio context is resumed after initialization
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
              audioContextRef.current.resume().catch(console.error);
            }
          }).catch(error => {
            console.error('Failed to initialize audio analysis:', error);
            setIsInitialized(false);
          });
        } else {
          // Not playing yet, but mark as ready to show watermark
          // Don't initialize audio context yet, just allow drawing
          setIsInitialized(true);
        }
      } else if (isInitialized && audioContextRef.current) {
        // Resume context if already initialized and playing
        if (isPlaying && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume().catch(console.error);
        }
      }
    } else {
      // No audio element - mark as initialized to allow watermark display
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Make canvas responsive
    const updateCanvasSize = () => {
      const currentCanvas = canvasRef.current;
      if (!currentCanvas) return;
      const container = currentCanvas.parentElement;
      if (container) {
        const containerWidth = container.clientWidth || 400;
        currentCanvas.width = containerWidth;
        currentCanvas.height = height;
      }
    };

    // Update canvas size on mount and resize
    updateCanvasSize();
    const resizeObserver = new ResizeObserver(() => {
      updateCanvasSize();
    });
    const parentElement = canvas.parentElement;
    if (parentElement) {
      resizeObserver.observe(parentElement);
    }

    // Always draw visualization, even if analyser is not ready
    // This ensures watermark is visible when audio is not playing
    const draw = () => {
      const currentCanvas = canvasRef.current;
      if (!currentCanvas) {
        animationFrameRef.current = null;
        return;
      }

      const currentCtx = currentCanvas.getContext('2d');
      if (!currentCtx) {
        animationFrameRef.current = null;
        return;
      }

      // Update canvas size if needed
      updateCanvasSize();

      currentCtx.fillStyle = '#f7fafc';
      currentCtx.fillRect(0, 0, currentCanvas.width, currentCanvas.height);

      const barWidth = currentCanvas.width / barCount;
      const barSpacing = barWidth * 0.2;

      // Check if audio is paused (not playing but has progress)
      const isPaused = !isPlaying && audioElement && audioElement.paused && audioElement.currentTime > 0 && !audioElement.ended;
      
      // Get audio data if analyser is available and playing
      let dataArray: Uint8Array | null = null;
      let bufferLength = barCount;
      let hasAudioData = false;
      
      if (audioElement && analyserRef.current && isPlaying && !audioElement.paused && !audioElement.ended) {
        bufferLength = analyserRef.current.frequencyBinCount;
        dataArray = new Uint8Array(bufferLength);
        try {
          // Type assertion for TypeScript compatibility
          analyserRef.current.getByteFrequencyData(dataArray as any);
          // Check if we have actual audio data (not all zeros)
          hasAudioData = dataArray.some(val => val > 0);
          // Store the frame when playing so we can use it when paused
          if (hasAudioData) {
            lastFrameRef.current = new Uint8Array(dataArray);
          }
        } catch (e) {
          // If CORS error, fill with zeros
          if (dataArray) {
            dataArray.fill(0);
          }
        }
      } else if (isPaused && lastFrameRef.current) {
        // When paused, use the last stored frame
        dataArray = lastFrameRef.current;
        bufferLength = dataArray.length;
        hasAudioData = dataArray.some(val => val > 0);
      }

      // Draw bars
      for (let i = 0; i < barCount; i++) {
        let barHeight = 0;
        
        if (dataArray && analyserRef.current && hasAudioData) {
          const dataIndex = Math.floor((i / barCount) * bufferLength);
          const dataValue = dataArray[dataIndex] ?? 0;
          barHeight = (dataValue / 255) * currentCanvas.height * 0.8;
        } else {
          // Show empty bars when not playing or analyser not ready
          barHeight = 0;
        }

        // Create gradient for visual appeal
        const gradient = currentCtx.createLinearGradient(
          i * barWidth,
          0,
          i * barWidth,
          currentCanvas.height
        );
        gradient.addColorStop(0, '#3b82f6');
        gradient.addColorStop(0.5, '#8b5cf6');
        gradient.addColorStop(1, '#ec4899');

        currentCtx.fillStyle = gradient;
        currentCtx.fillRect(
          i * barWidth + barSpacing,
          currentCanvas.height - barHeight,
          barWidth - barSpacing * 2,
          barHeight
        );
      }

      // Draw watermark when audio is stopped/not started/finished (but NOT when paused)
      // When paused, we keep the visualization bars visible (just not animated)
      const isStopped = !audioElement || (audioElement && audioElement.ended) || (!isPlaying && audioElement && audioElement.currentTime === 0 && !isPaused);
      const isNotStarted = !isPlaying && audioElement && audioElement.currentTime === 0 && !isPaused;
      const noAudioElement = !audioElement;
      
      // Show watermark when stopped/not started/finished, NOT when paused
      // Also show if no analyser is initialized yet (initial state) or no audio element
      const shouldShowWatermark = (noAudioElement || isStopped || isNotStarted || !analyserRef.current) && !isPaused && !hasAudioData;
      
      if (shouldShowWatermark) {
        currentCtx.save();
        currentCtx.globalAlpha = 0.3;
        currentCtx.fillStyle = '#6b7280';
        currentCtx.font = `bold ${Math.max(12, currentCanvas.height * 0.25)}px system-ui, -apple-system, sans-serif`;
        currentCtx.textAlign = 'center';
        currentCtx.textBaseline = 'middle';
        const watermarkText = 'Audio Visualization';
        currentCtx.fillText(watermarkText, currentCanvas.width / 2, currentCanvas.height / 2);
        currentCtx.restore();
      }

      // Continue animation loop to keep visualization visible
      animationFrameRef.current = requestAnimationFrame(draw);
    };

    // Start drawing loop
    if (!animationFrameRef.current) {
      draw();
    }

    // Cleanup resize observer and event listeners
    return () => {
      resizeObserver.disconnect();
      if (cleanup) {
        cleanup();
      }
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [audioElement, isPlaying, isInitialized, barCount, canVisualize, height]);

  // Cleanup on unmount or when audio element changes
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch (e) {
          // Ignore disconnect errors (element might already be disconnected)
        }
        sourceRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(console.error);
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      setIsInitialized(false);
    };
  }, [audioElement]); // Re-run cleanup when audio element changes

  // Always render the canvas, even if visualization is disabled (to show watermark)
  // The draw loop will handle showing appropriate content

  return (
    <div className={`audio-visualizer ${className}`} style={{ height: `${height}px`, width: '100%', minWidth: '200px' }}>
      <canvas
        ref={canvasRef}
        width={400}
        height={height}
        className="w-full h-full rounded"
        style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
      />
    </div>
  );
};

export default AudioVisualizer;

